import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SeoHelmet from '@/components/feature/SeoHelmet';
import { comics, comicCategories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = useMemo(() => comics.find((c) => c.id === Number(id)), [id]);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500 text-lg">漫画未找到</p>
            <Link
              to="/"
              className="inline-block mt-4 px-6 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryInfo = comicCategories.find((c) => c.id === comic.category);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: categoryInfo?.name || '分类', path: `/category/${comic.category}` },
    { label: comic.title },
  ];

  const chapterList = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    number: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: `2026-${String(8 - Math.floor(i / 4)).padStart(2, '0')}-${String(15 - (i % 4) * 3).padStart(2, '0')}`,
  }));

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const pageSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${comic.title} - 丫丫漫画在线免费阅读`,
      description: comic.description,
      author: { '@type': 'Person', name: comic.author },
      datePublished: '2024-01-01',
      dateModified: '2026-08-04',
      image: comic.cover,
      publisher: {
        '@type': 'Organization',
        name: '丫丫漫画',
      },
      inLanguage: 'zh-CN',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SeoHelmet
        title={`${comic.title} | 丫丫漫画在线免费阅读`}
        description={comic.description}
        keywords={`丫丫漫画,${comic.title},${comic.author},${comic.tags.join(',')},在线免费阅读,下拉式漫画`}
        canonicalPath={`/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        schema={pageSchema}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 丫丫漫画`}
                    title={`${comic.title} - 丫丫漫画在线免费阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
                  {comic.title}
                </h1>
                <p className="mt-1 text-sm text-foreground-500">作者：{comic.author}</p>

                <div className="flex items-center flex-wrap gap-2 mt-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === '已完结' ? 'bg-secondary-100 text-secondary-700' : 'bg-primary-100 text-primary-700'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-6 mt-4 text-sm text-foreground-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {comic.views} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-500"></i>
                    {comic.rating} 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-file-list-3-line"></i>
                    共 {comic.chapters} 话
                  </span>
                </div>

                <p className="mt-4 text-sm text-foreground-700 leading-relaxed">
                  {comic.description}
                </p>

                <div className="flex flex-wrap gap-3 mt-6">
                  <button className="px-8 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    开始阅读
                  </button>
                  <button className="px-8 py-3 rounded-lg bg-background-100 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line mr-1"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl font-heading text-foreground-950 mb-6">章节目录</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {chapterList.map((ch) => (
                <button
                  key={ch.number}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 hover:bg-primary-50 text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer text-left"
                >
                  <span className="truncate">{ch.title}</span>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{ch.date}</span>
                </button>
              ))}
            </div>

            {comic.chapters > 20 && (
              <div className="text-center mt-6">
                <button className="px-8 py-2.5 rounded-lg bg-background-50 text-foreground-600 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  查看全部 {comic.chapters} 话
                </button>
              </div>
            )}
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 bg-background-50">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl font-heading text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-product-shop="">
                {relatedComics.map((c) => (
                  <Link
                    key={c.id}
                    to={`/comic/${c.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="aspect-[3/4] rounded-lg overflow-hidden mb-2">
                      <img
                        src={c.cover}
                        alt={`${c.title} - 丫丫漫画`}
                        title={`${c.title} - 丫丫漫画在线免费阅读`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {c.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{c.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}